"use client";

import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Box } from "@dxp-feed/ui/Box";
import { Text } from "@dxp-feed/ui/Text";
import "@/styles/home.css";
import { Columns } from "@dxp-feed/ui/Columns";
import { Row } from "@dxp-feed/ui/Row";
import { Index, useSearchBox } from "@dxp-feed/algolia";
import { algoliaConfig } from "@/services/algolia/config";

import { usePathname, useRouter, useSearchParams } from "next/navigation";
import {
  GLOBAL_SEARCH_QUERY_PARAM_KEY,
  GLOBAL_SEARCH_RESULTS_SECTIONS,
  SEARCH_RESULT_TAB_QUERY_PARAM_KEY,
} from "@/utils/constant";
import RecipeList from "@/templates/recipes/RecipeListing";
import ArticleList from "@/templates/articles/ArticleListing";
import TabView from "./TabView";
import {
  isNotInEnum,
  SanitizeGlobalSearchInput,
} from "@/helpers/useCommonFunctions";
import ProductListingMain from "@/templates/products/ProductsListingMain";
import AlgoliaMiddleware from "@/templates/algolia/Middleware";
import { NavigateOptions } from "next/dist/shared/lib/app-router-context.shared-runtime";
import { SiteContainer } from "@/components/Container";
import NoResults from "./NoResults";
import Loader from "@/components/Loader";
import { useAppSelector } from "@/utils/hooks/reactUseCustomHooks";
import { tabsResult } from "../../styles";
import AdSlot from "@/helpers/gpt";
import { adsScript } from "@/utils/adsTempConstant";
import { hasProfanity } from "@dxp-feed/algolia/dist/services/profanity";
import BreadcrumbWrapper from "@/components/Breadcrumb";
import { replaceAEMStaticText } from "@/helpers/global";
import useWindowSize from "@/utils/hooks/useWindowSize";
import { FetchDietaryPreferencesResults } from "@/services/algolia";
import { useStoreDetails } from "@/utils/hooks/useStoreDetails";
import { GlobalSearchResults } from "@/utils/types/algolia";

export default function SearchTabs() {
  const breadcrumb = [
    { text: "Home", href: "/" },
    { text: "Search Results", href: null },
  ];

  const { storeID } = useStoreDetails();
  const windowSize = useWindowSize();
  const searchParams = new URLSearchParams(useSearchParams());
  const prevQuery = useRef<string>("");
  const { refine: searchRefine } = useSearchBox();
  const router = useRouter();
  const pathname = usePathname();

  const [loading, setLoading] = useState(true);
  const [resultCounterPerIndex, setResultCounterPerIndex] = useState<
    Record<GLOBAL_SEARCH_RESULTS_SECTIONS, number>
  >({
    [GLOBAL_SEARCH_RESULTS_SECTIONS.PRODUCT]: 0,
    [GLOBAL_SEARCH_RESULTS_SECTIONS.RECIPE]: 0,
    [GLOBAL_SEARCH_RESULTS_SECTIONS.ARTICLE]: 0,
  });
  const [preferredResults, setPreferredResults] =
    useState<GlobalSearchResults | null>(null);

  const path = useAppSelector((state) => state?.adsSclice?.page ?? "");
  const userSession = useAppSelector((state) => state.gigyaslice);
  const userId = userSession?.uid ?? undefined;
  const { resultsPage: staticContent, loading: isStaticContentLoading } =
    useAppSelector((state) => state.aemStaticSearchData);

  const hasMinimumResults = useCallback(() => {
    return (
      String(resultCounterPerIndex[GLOBAL_SEARCH_RESULTS_SECTIONS.PRODUCT])
        .length >= 3 ||
      String(resultCounterPerIndex[GLOBAL_SEARCH_RESULTS_SECTIONS.RECIPE])
        .length >= 3 ||
      String(resultCounterPerIndex[GLOBAL_SEARCH_RESULTS_SECTIONS.ARTICLE])
        .length >= 3
    );
  }, [resultCounterPerIndex]);

  const searchQuery = SanitizeGlobalSearchInput(
    searchParams.get(GLOBAL_SEARCH_QUERY_PARAM_KEY) ?? ""
  );

  const currentTab =
    (searchParams.get(
      SEARCH_RESULT_TAB_QUERY_PARAM_KEY
    ) as GLOBAL_SEARCH_RESULTS_SECTIONS) ||
    GLOBAL_SEARCH_RESULTS_SECTIONS.PRODUCT;

  const tabList = useMemo(
    () => [
      {
        label: staticContent?.productsTabTitle ?? "Products",
        id: "products",
        tab: GLOBAL_SEARCH_RESULTS_SECTIONS.PRODUCT,
        count: resultCounterPerIndex.products,
        index: algoliaConfig.indexNames.products,
        children: (
          <ProductListingMain
            isSearchPage={true}
            searchQuery={searchQuery}
            preferredResults={preferredResults?.products?.hits}
          />
        ),
      },
      {
        label: staticContent?.recipesTabTitle ?? "Recipes",
        id: "recipes",
        tab: GLOBAL_SEARCH_RESULTS_SECTIONS.RECIPE,
        count: resultCounterPerIndex.recipes,
        index: algoliaConfig.indexNames.recipes,
        children: (
          <RecipeList
            isSearchPage={true}
            searchQuery={searchQuery}
            preferredResults={preferredResults?.recipes?.hits}
          />
        ),
      },
      {
        label: staticContent?.articlesTabTitle ?? "Articles",
        id: "articles",
        tab: GLOBAL_SEARCH_RESULTS_SECTIONS.ARTICLE,
        count: resultCounterPerIndex.articles,
        index: algoliaConfig.indexNames.articles,
        children: (
          <ArticleList
            isSearchPage={true}
            searchQuery={searchQuery}
            preferredResults={preferredResults?.articles?.hits}
          />
        ),
      },
    ],
    [resultCounterPerIndex, searchQuery, staticContent, preferredResults]
  );

  const totalResultCount = useMemo(
    () =>
      Object.values(resultCounterPerIndex).reduce(
        (prev, curr) => prev + curr,
        0
      ),
    [resultCounterPerIndex]
  );

  const changeCounterPerIndex = (
    key: GLOBAL_SEARCH_RESULTS_SECTIONS,
    val: number
  ) => {
    setResultCounterPerIndex((prev) => ({ ...prev, [key]: val }));
  };

  const FetchDietaryPreferencesResultsForQuery = async () => {
    try {
      if (userSession?.lifestyles) {
        const response = await FetchDietaryPreferencesResults(
          storeID,
          searchQuery,
          userSession.lifestyles
        );
        setPreferredResults(response || null);
      } else {
        setPreferredResults(null);
      }
    } catch (error) {
      console.error("Error fetching dietary preferences results:", error);
      setPreferredResults(null);
    }
  };

  // Update search query.
  useEffect(() => {
    if (prevQuery.current !== searchQuery) {
      searchRefine(searchQuery ?? "");
    }
    if (storeID && userSession?.lifestyles) {
      FetchDietaryPreferencesResultsForQuery();
    }
  }, [searchQuery, storeID, userSession]);

  // Incorrect tabs.
  useEffect(() => {
    if (isNotInEnum(GLOBAL_SEARCH_RESULTS_SECTIONS, currentTab)) {
      searchParams.set(
        SEARCH_RESULT_TAB_QUERY_PARAM_KEY,
        GLOBAL_SEARCH_RESULTS_SECTIONS.PRODUCT
      );
      routeWithSearchParams(searchParams, router.replace.bind(router));
    }
  }, [currentTab]);

  const routeWithSearchParams = (
    params: URLSearchParams,
    event: (href: string, options?: NavigateOptions) => void = router.push.bind(
      router
    )
  ): void => {
    // Keep only 'query' and 'tab' parameters
    const filteredParams = new URLSearchParams();
    const allowedKeys = [
      GLOBAL_SEARCH_QUERY_PARAM_KEY,
      SEARCH_RESULT_TAB_QUERY_PARAM_KEY,
    ];
    allowedKeys.forEach((key) => {
      if (params.has(key)) {
        filteredParams.set(key, params.get(key)!);
      }
    });
    event(`${pathname}?${filteredParams.toString()}`, { scroll: false });
  };

  const switchTab = useCallback(
    (value: GLOBAL_SEARCH_RESULTS_SECTIONS, replaceTab: boolean = false) => {
      searchParams.set(SEARCH_RESULT_TAB_QUERY_PARAM_KEY, value);
      routeWithSearchParams(
        searchParams,
        replaceTab ? router.replace.bind(router) : undefined
      );
    },
    [searchParams, router]
  );

  const indexes = useMemo(
    () =>
      tabList.map((item) => ({
        label: item.id,
        name: item.index,
      })),
    [tabList]
  );

  const getResultsMessage = (
    hasProfanity: boolean,
    searchQuery: string,
    totalResultCount: number,
    loading: boolean
  ) => {
    const template = hasProfanity
      ? staticContent?.searchResultsMsgForProfanity
      : staticContent?.searchResultsMsg;

    const commonReplacementObj = {
      resultsCount: loading ? "Fetching" : totalResultCount,
    };

    const replacements = hasProfanity
      ? commonReplacementObj
      : { ...commonReplacementObj, searchQuery };

    const message = replaceAEMStaticText(template, replacements);
    return message?.replace(
      "results",
      `result${totalResultCount !== 1 ? "s" : ""}`
    );
  };
  return (
    <>
      {isStaticContentLoading || userSession?.isLoading || !storeID ? (
        <Loader />
      ) : (
        <Box
          className={`h-full min-h-[400px] ${!staticContent ? "hidden" : "visible"}`}
          data-testid="search-tab"
        >
          <Box className={`${!searchQuery.length ? "mb-[24px]" : ""} `}>
            <SiteContainer>
              <BreadcrumbWrapper breadcrumbList={breadcrumb} />
            </SiteContainer>
            <AlgoliaMiddleware
              userId={userId}
              indexes={indexes}
              setLoading={setLoading}
              prevQuery={prevQuery}
              pageType="SEARCH_RESULT"
              currentTab={currentTab}
            />
            <SiteContainer className="[&_div>div>iframe]:rounded-[12px]">
              <AdSlot
                id={adsScript?.[path]?.leaderboardTop_A?.id}
                // width={screenWidth ? screenWidth-80 + "px" : "0px"}
              />
            </SiteContainer>

            <SiteContainer className="[&_div>div>iframe]:rounded-[12px]">
              <AdSlot id={adsScript?.[path]?.leaderboardTop_B?.id} />
            </SiteContainer>
            <Box
              as="div"
              className="max-w-[320px] md:max-w-[728px] lg:max-w-[970px] m-auto [&_iframe]:m-auto"
            >
              <AdSlot id={adsScript?.[path]?.leaderboardTop_C?.id} />
            </Box>
          </Box>
          <Box as="div">
            <SiteContainer>
              {totalResultCount > 0 && searchQuery.length ? (
                <Row>
                  <Columns className="col-12 ">
                    <Box
                      as="div"
                      className="w-full text-center py-[24px] 2md:mb-[24px] 2lg:mb-[48px]"
                    >
                      <Text
                        as="h1"
                        className="text-center text-2xl xs:text-3xl font-sans w-full break-words"
                      >
                        {getResultsMessage(
                          hasProfanity(searchQuery),
                          searchQuery,
                          totalResultCount,
                          loading
                        )}
                      </Text>
                    </Box>
                  </Columns>
                </Row>
              ) : null}
            </SiteContainer>

            <SiteContainer
              className={`w-full ${!searchQuery.length ? "mt-[16px] 2md:mt-[24px] xl:mt-[48px]" : " px-0  lg:px-12"} ${resultCounterPerIndex[currentTab] < 1 ? "!pb-0 px-0 md:px-0 2md:px-12 [&_.row]:mx-0 test-popular [&_.col-12]:px-0 " : " px-0  lg:px-12 "}`}
            >
              <Row
                className={` ${resultCounterPerIndex[currentTab] < 1 ? "" : "!mx-0"}`}
              >
                <Columns
                  className={`col-12 ${resultCounterPerIndex[currentTab] < 1 ? "" : "!px-0"}`}
                >
                  {(loading ||
                    userSession?.isLoading ||
                    (!loading && totalResultCount > 0)) && (
                    <>
                      <Box
                        as="div"
                        id="Search_products"
                        className={`flex md:w-fit md:justify-start justify-center   px-6 2md:px-0  [&>[aria-selected=true]]:border-b-grey350 [&>[aria-selected=true]]:border-b-2 [&>[aria-selected=true]]:!text-brandBlack `}
                      >
                        {tabList.map((item) => (
                          <Box
                            className={"w-1/3 xs:w-auto"}
                            as="button"
                            onClick={() => {
                              if (currentTab !== item.tab) {
                                switchTab(item.tab);
                              }
                            }}
                            data-testid={`${item.tab}-tab-button`}
                            key={item.tab}
                          >
                            <Box
                              aria-selected={currentTab === item.tab}
                              className={`aria-selected:bg-brandSecondary aria-selected:relative aria-selected:rounded-t-lg aria-selected:text-brandWhite bg-transparent py-[14px] px-[12px] border-b-transparent border-b-[2px] max-w-[unset]
                              text-body md:min-w-[145px] text-brandBlack text-center xs:min-w-[144px] min-w-[90px] min-w-auto aria-selected:after:content-[''] aria-selected:after:w-full aria-selected:after:h-[3px] aria-selected:after:bg-brandSecondary 
                              aria-selected:after:absolute aria-selected:after:bottom-[-3px] aria-selected:after:left-0 aria-selected:after:right-0
                              flex items-center h-full justify-center ${hasMinimumResults() ? "!block xs:flex" : "flex"}`}
                            >
                              <span
                                className={
                                  hasMinimumResults()
                                    ? "block xs:inline-block  "
                                    : "inline-block"
                                }
                              >
                                {item.label}
                              </span>
                              {item.count > 0 && (
                                <span className="ml-1">
                                  {!loading &&
                                  !userSession?.isLoading &&
                                  item.count > 0
                                    ? "(" + item.count + ")"
                                    : ""}
                                </span>
                              )}
                            </Box>
                          </Box>
                        ))}
                      </Box>
                      <Box
                        as="div"
                        className="flex border-b-grey350 border-b-[1px] mb-[0] lg:mb-[32px]"
                      ></Box>
                    </>
                  )}

                  {(loading || userSession?.isLoading) && (
                    <Loader className="fixed inset-0 bg-[#fff] bg-opacity-70 z-[9999] flex items-center justify-center" />
                  )}
                  <Box as="div" className={`w-full`}>
                    {tabList.map((item) => (
                      <Box
                        aria-selected={currentTab === item.tab}
                        className={`${item.count || (!searchQuery?.length && currentTab === item.tab) ? "aria-selected:block hidden" : "hidden"} ${tabsResult} ${!searchQuery.length ? "[&_.main-wrapper]:lg:mt-0" : " "}`}
                        aria-label={`${item.label} List`}
                        key={item.tab}
                      >
                        {!searchQuery.length ? (
                          <Box className="px-6 2md:px-0">
                            <Row>
                              <Columns className="col-12">
                                <Text
                                  as="h1"
                                  className="mt-[16px] mb-[16px]  2md:mb-[32px] 2md:mt-[32px] lg:mt-0 xs:mt-[24px] xs:mb-[24px] leading-[42px] text-3xl font-bold"
                                >
                                  Popular {item.label}
                                </Text>
                              </Columns>
                            </Row>
                          </Box>
                        ) : null}
                        <Index indexName={item.index}>
                          <TabView
                            searchQuery={searchQuery}
                            title={item.tab}
                            changeCounterPerIndex={changeCounterPerIndex}
                          >
                            {item.children}
                          </TabView>
                        </Index>
                      </Box>
                    ))}
                  </Box>
                </Columns>
              </Row>
            </SiteContainer>
          </Box>
          {!loading && searchQuery?.length ? (
            <SiteContainer>
              <Row>
                <Columns>
                  {resultCounterPerIndex[currentTab] < 1 && (
                    <Box className="w-full pb-[24px] xs:pb-[48px]">
                      <NoResults
                        searchQuery={searchQuery.trim()}
                        currentTab={currentTab}
                        totalCount={totalResultCount}
                        staticContent={staticContent}
                        isGlobalSearch={false}
                        showControls={windowSize.width >= 1024}
                        isSearchPage
                      />
                    </Box>
                  )}
                </Columns>
              </Row>
            </SiteContainer>
          ) : null}
        </Box>
      )}
    </>
  );
}
